import { useCallback, useRef, useState } from 'react';
import {
  MOBILE_EDGE_TAB_HEIGHT,
  mobileEdgeTabBottomWithOffset,
  type MobileEdgeDockSlot,
} from '../components/layout/floatingDockMobileEdge';

const STORAGE_PREFIX = 'klinvia.mobileEdge.bottomOffset.';
const DRAG_THRESHOLD_PX = 8;

function readStoredOffset(slot: MobileEdgeDockSlot): number {
  if (typeof window === 'undefined') return 0;
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${slot}`);
    const n = raw != null ? Number(raw) : 0;
    return Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0;
  } catch {
    return 0;
  }
}

function writeStoredOffset(slot: MobileEdgeDockSlot, px: number): void {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${slot}`, String(Math.max(0, Math.round(px))));
  } catch {
    /* ignore */
  }
}

function maxDragOffsetPx(): number {
  if (typeof window === 'undefined') return 360;
  const vh = window.visualViewport?.height ?? window.innerHeight;
  const tabH = MOBILE_EDGE_TAB_HEIGHT;
  const topClearance = 56;
  const baseBottom = 112;
  const ceiling = Math.max(0, vh - tabH - topClearance - baseBottom);
  return Math.min(480, Math.max(120, Math.round(ceiling)));
}

export function useMobileEdgeTabDragOffset(slot: MobileEdgeDockSlot) {
  const [offsetPx, setOffsetPx] = useState(() => readStoredOffset(slot));
  const offsetRef = useRef(offsetPx);
  offsetRef.current = offsetPx;

  const sessionRef = useRef<{ startY: number; startOffset: number } | null>(null);
  const didDragRef = useRef(false);
  const draggingRef = useRef(false);

  const bottomCss = mobileEdgeTabBottomWithOffset(slot, offsetPx);

  const bindDragSurface = useCallback(() => {
    const onPointerDown = (event: React.PointerEvent<HTMLElement>) => {
      if (event.button !== 0) return;
      sessionRef.current = { startY: event.clientY, startOffset: offsetRef.current };
      didDragRef.current = false;
      draggingRef.current = false;
      event.currentTarget.setPointerCapture(event.pointerId);
    };

    const onPointerMove = (event: React.PointerEvent<HTMLElement>) => {
      const session = sessionRef.current;
      if (!session) return;
      const dy = session.startY - event.clientY;
      if (!draggingRef.current && Math.abs(dy) >= DRAG_THRESHOLD_PX) {
        draggingRef.current = true;
        didDragRef.current = true;
      }
      if (!draggingRef.current) return;
      event.preventDefault();
      const max = maxDragOffsetPx();
      const next = Math.min(max, Math.max(0, session.startOffset + dy));
      setOffsetPx(next);
    };

    const finish = (event: React.PointerEvent<HTMLElement>) => {
      if (draggingRef.current) {
        writeStoredOffset(slot, offsetRef.current);
      }
      sessionRef.current = null;
      draggingRef.current = false;
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        /* already released */
      }
    };

    return {
      onPointerDown,
      onPointerMove,
      onPointerUp: finish,
      onPointerCancel: finish,
    };
  }, [slot]);

  const consumeClickIfDragged = useCallback((handler: () => void) => {
    return () => {
      if (didDragRef.current) {
        didDragRef.current = false;
        return;
      }
      handler();
    };
  }, []);

  return {
    offsetPx,
    bottomCss,
    isDragging: draggingRef,
    bindDragSurface,
    consumeClickIfDragged,
  };
}
